import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Message, Modal } from '@bedrock/components';
import { Check, Undo } from '@bedrock/icons-react';
import RechargeDrawer, { rechargeFooterButtonProps } from './RechargeDrawer';
import withFunctionCall from '@mpc/common/src/utils/withFunctionCall';
import SFormItem from '#components/SForm/SFormItem';
import CenterLoading from '#components/CenterLoading';
import TextAreaWithUpload from '#components/SForm/SFormElement/TextAreaWithUpload';
import SendBackModal from '#features/Refund/SendBackModal';
import VerticalLineTitle from '#components/VerticalLineTitle';
import { Z_INDEX } from '#utils/zIndex';
import {
  fetchRechargeDetail,
  confirmRecharge,
  abandonRecharge,
  extractAttachmentIds,
} from '../rechargeService';
import { RechargeStatusTag } from './RechargeStatusTag';
import RechargeFormModel, {
  VIRTUAL_KEY_ORDER_AMOUNT,
} from '../RechargeFormModel';
import { buildRechargeConfirmValues } from '../hooks/buildValues';
import {
  FIELD_KEYS,
  RECHARGE_STATUS_DESC,
  RECHARGE_PENDING_INITIATE_STATUSES,
  ATTACHMENT_MAX_SIZE,
  CHANNEL_L1_CATEGORIES,
  PLATFORM_L1_CATEGORIES,
} from '../constants';
import { reasonFieldValidator } from '../formConfig';
import type {
  RechargeConfirmActionEnum,
  RechargeOrderDetailVo,
  RechargeStateEnum,
} from '#services/api';

export interface RechargeConfirmViewProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess?: () => void;
  orderId: number;
  /** 外包（供应商端）标记：仅 SUPPLIER_CONFIRM 可操作，其余确认节点只读「等待内部审批」 */
  isExternal?: boolean;
}

/**
 * 充值单确认页（审批详情 + 按状态/角色展示确认操作面板），内/外包共用。
 *
 * 内包 Confirm 与外包 Detail 原为两套重复容器，收敛到此共享组件，用 `isExternal` 区分角色范围：
 * - 内包（isExternal=false）：
 *   - PLANNER_CONFIRM：策划代填超充/提前充值原因（is_over_charge/is_early_recharge 控制显隐）
 *   - SUPPLIER_CONFIRM：上传充值附件必填
 *   - OPTIMIZER/PRINCIPAL_CONFIRM：只读，核对后确认通过
 * - 外包（isExternal=true，供应商视角）：
 *   - SUPPLIER_CONFIRM：上传充值附件必填
 *   - PLANNER/OPTIMIZER/PRINCIPAL_CONFIRM：只读「等待内部审批，供应商无需操作」
 *
 * API 层 rechargeService 已按 $sType 自动分流内/外包接口，前端无需传 isExternal 决定接口。
 * 详情展示对齐退款 FormDrawer view：顶部状态 Tag + Descriptions 两列详情。
 */
export const RechargeConfirmView: React.FC<RechargeConfirmViewProps> = ({
  visible,
  onCancel,
  onSuccess,
  orderId,
  isExternal = false,
}) => {
  const [form] = Form.useForm();
  const [detail, setDetail] = useState<RechargeOrderDetailVo>();
  const [loading, setLoading] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const [backVisible, setBackVisible] = useState(false);

  const load = () => {
    if (!orderId) return;
    let active = true;
    setLoading(true);
    fetchRechargeDetail({ order_id: orderId }, { alertError: false })
      .then((res) => {
        if (!active) return;
        if (res?.code === 1 && res.data) {
          const d = res.data as RechargeOrderDetailVo;
          setDetail(d);
          // 回填详情只读字段（currencyType/input view 读 form 值）+ 原因/附件（确认操作字段）。
          // 超充/提前原因仅内包策划确认节点消费，外包供应商不渲染；统一回填无副作用。
          form.setFieldsValue({
            ...buildRechargeConfirmValues(d),
            [FIELD_KEYS.IS_OVER_CHARGE]: d.is_over_charge,
            [FIELD_KEYS.IS_EARLY_RECHARGE]: d.is_early_recharge,
            [FIELD_KEYS.OVER_CHARGE_REASON]: {
              text: d.over_charge_reason || '',
              files: d.over_charge_attachments || [],
              itemKey: FIELD_KEYS.OVER_CHARGE_REASON,
            },
            [FIELD_KEYS.EARLY_RECHARGE_REASON]: {
              text: d.early_recharge_reason || '',
              files: d.early_recharge_attachments || [],
              itemKey: FIELD_KEYS.EARLY_RECHARGE_REASON,
            },
            [FIELD_KEYS.ATTACHMENT]: {
              text: d.recharge_remark || '',
              files: d.supplier_confirm_attachments || [],
              itemKey: FIELD_KEYS.ATTACHMENT,
            },
          });
        } else {
          Message.error(res?.message || '加载详情失败');
        }
      })
      .catch((err) => {
        if (!active) return;
        Message.error(err?.message || '加载详情失败');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const status = detail?.status;
  const isPlannerConfirm = status === 'PLANNER_CONFIRM';
  const isSupplierConfirm = status === 'SUPPLIER_CONFIRM';
  const isReadonlyConfirm =
    status === 'OPTIMIZER_CONFIRM' || status === 'PRINCIPAL_CONFIRM';
  // 操作权限由后端 detail 接口 permitAction 返回（按当前用户是否待办人 + 状态节点计算），前端据此控制按钮显隐
  const permitAction = detail?.permit_action;
  const canConfirm = permitAction?.can_confirm === true;
  const canReturn = permitAction?.can_return === true;
  const canAbandon = permitAction?.can_abandon === true;

  // 待发起节点（待 XX 发起）：canConfirm=true 时按钮显示「重新发起」（走 confirm 接口），而非「确认通过」
  const isPendingInitiate =
    !!status && RECHARGE_PENDING_INITIATE_STATUSES.includes(status as never);
  // 策划发起（待策划发起节点重新发起时需填超充/提前原因，镜像后端 handlePlannerReasonIfInitiatedByPlanner）
  const isPlannerInitiator = detail?.initiator_role === 'PLANNER';
  // 策划原因字段显隐：确认节点的策划代填 或 待策划发起节点的策划重新发起
  const showPlannerReason = isPlannerConfirm || (isPendingInitiate && isPlannerInitiator);

  // 后端 detail VO 已返回 is_over_charge/is_early_recharge 标志，精确判断显隐
  const hasOverCharge = detail?.is_over_charge === true;
  const hasEarlyRecharge = detail?.is_early_recharge === true;

  const handleConfirm = async () => {
    try {
      setBtnLoading(true);
      const cmd: {
        recharge_order_id: number;
        action: RechargeConfirmActionEnum;
        amount?: number;
        account_id_name?: string;
        channel?: string;
        platform?: string;
        optimizer_ids?: number[];
        over_charge_reason?: string;
        over_charge_attachment_ids?: number[];
        early_recharge_reason?: string;
        early_recharge_attachment_ids?: number[];
        attachment_ids?: number[];
        recharge_remark?: string;
      } = { recharge_order_id: orderId, action: 'CONFIRM' };

      if (isPendingInitiate) {
        // 待发起节点重新发起：可编辑字段回传（后端 applyResubmitFields 覆盖式 set，须带原值避免清空）
        // + 策划发起重填超充/提前原因（镜像 RechargeFormView resubmit）
        cmd.amount = detail?.amount;
        cmd.account_id_name = detail?.account_id_name;
        cmd.channel = detail?.channel;
        cmd.platform = detail?.platform;
        cmd.optimizer_ids = (detail?.optimizers || [])
          .map((o) => o?.id)
          .filter((id): id is number => id != null);
        if (isPlannerInitiator) {
          const values = await form.validateFields();
          const over = values[FIELD_KEYS.OVER_CHARGE_REASON] as {
            text?: string;
            files?: unknown[];
          };
          const early = values[FIELD_KEYS.EARLY_RECHARGE_REASON] as {
            text?: string;
            files?: unknown[];
          };
          if (hasOverCharge) {
            cmd.over_charge_reason = over?.text || '';
            cmd.over_charge_attachment_ids = extractAttachmentIds(over);
          }
          if (hasEarlyRecharge) {
            cmd.early_recharge_reason = early?.text || '';
            cmd.early_recharge_attachment_ids = extractAttachmentIds(early);
          }
        }
      } else if (!isExternal && isPlannerConfirm) {
        // 策划确认节点（仅内包）：策划代填超充/提前充值原因
        const values = await form.validateFields();
        const over = values[FIELD_KEYS.OVER_CHARGE_REASON] as {
          text?: string;
          files?: unknown[];
        };
        const early = values[FIELD_KEYS.EARLY_RECHARGE_REASON] as {
          text?: string;
          files?: unknown[];
        };
        if (hasOverCharge) {
          cmd.over_charge_reason = over?.text || '';
          cmd.over_charge_attachment_ids = extractAttachmentIds(over);
        }
        if (hasEarlyRecharge) {
          cmd.early_recharge_reason = early?.text || '';
          cmd.early_recharge_attachment_ids = extractAttachmentIds(early);
        }
      } else if (isSupplierConfirm) {
        // 供应商确认节点（内/外包共用）：充值说明 与 充值附件 二选一必填
        const values = await form.validateFields();
        const att = values[FIELD_KEYS.ATTACHMENT] as {
          text?: string;
          files?: unknown[];
        };
        cmd.attachment_ids = extractAttachmentIds(att);
        cmd.recharge_remark = att?.text?.trim() || undefined;
      }

      const res = await confirmRecharge(cmd, { silent: true });
      if (res?.code === 1) {
        Message.success(isPendingInitiate ? '重新发起成功' : '确认成功');
        onSuccess?.();
        onCancel();
      } else {
        Message.error(res?.message || '确认失败');
      }
    } catch (err: unknown) {
      const hasErrorFields =
        !!err &&
        typeof err === 'object' &&
        'errorFields' in err &&
        Array.isArray((err as { errorFields: unknown[] }).errorFields) &&
        (err as { errorFields: unknown[] }).errorFields.length > 0;
      if (!hasErrorFields) {
        Message.error((err as { message?: string })?.message || '确认失败');
      }
    } finally {
      setBtnLoading(false);
    }
  };

  const handleReject = async (reason: string) => {
    try {
      setBtnLoading(true);
      const res = await confirmRecharge(
        {
          recharge_order_id: orderId,
          action: 'REJECT',
          reject_reason: reason,
        },
        { silent: true }
      );
      if (res?.code === 1) {
        Message.success('已退回');
        setBackVisible(false);
        onSuccess?.();
        onCancel();
      } else {
        Message.error(res?.message || '退回失败');
      }
    } catch (err: unknown) {
      Message.error((err as { message?: string })?.message || '退回失败');
    } finally {
      setBtnLoading(false);
    }
  };

  const handleAbandon = () => {
    Modal.confirm({
      title: '确认废弃该充值单？',
      content: '废弃后该充值单作废，不可恢复。',
      // 高于 Drawer(zIndex 1030)，避免被抽屉盖住（rock-modal 默认 1000 < drawer）
      zIndex: Z_INDEX.modal,
      onOk: async () => {
        try {
          const res = await abandonRecharge(
            { order_id: orderId },
            { silent: true }
          );
          if (res?.code === 1) {
            Message.success('已废弃');
            onSuccess?.();
            onCancel();
          } else {
            Message.error(res?.message || '废弃失败');
          }
        } catch (err: unknown) {
          Message.error((err as { message?: string })?.message || '废弃失败');
        }
      },
    });
  };

  // 审批详情字段集合（只读 view）：对齐退款 FormDrawer view 的 itemCols 显式控制字段，
  // 不依赖 formConfig 发起态显隐规则（审批态与发起态字段集/显隐不同）
  const confirmItemCols = useMemo(() => {
    const l1 = detail?.l1_category_id;
    return [
      { formItemKey: FIELD_KEYS.ORDER_NO, view: true, show: true },
      { formItemKey: FIELD_KEYS.L2_DEMAND_ID, view: true, show: true },
      { formItemKey: FIELD_KEYS.L3_DEMAND_ID, view: true, show: true },
      { formItemKey: FIELD_KEYS.SUPPLIER_NAME, view: true, show: true },
      { formItemKey: VIRTUAL_KEY_ORDER_AMOUNT, view: true, show: true },
      { formItemKey: FIELD_KEYS.ACCOUNT_ID_NAME, view: true, show: true },
      ...(CHANNEL_L1_CATEGORIES.has(l1)
        ? [{ formItemKey: FIELD_KEYS.CHANNEL, view: true, show: true }]
        : []),
      ...(PLATFORM_L1_CATEGORIES.has(l1)
        ? [{ formItemKey: FIELD_KEYS.PLATFORM, view: true, show: true }]
        : []),
      { formItemKey: FIELD_KEYS.OPTIMIZER_IDS, view: true, show: true },
      // 是否超充/是否提前充值：仅内包 + 发起角色非供应商（PLANNER/OPTIMIZER）的单据展示；
      // 外包端 isExternal 直接排除（detail 不返回这两字段），内包供应商发起按 initiator_role 排除
      ...(!isExternal && detail?.initiator_role !== 'SUPPLIER'
        ? [
            { formItemKey: FIELD_KEYS.IS_OVER_CHARGE, view: true, show: true },
            { formItemKey: FIELD_KEYS.IS_EARLY_RECHARGE, view: true, show: true },
          ]
        : []),
      // 供应商确认节点：充值附件可编辑，并入详情网格（对齐退款 attachments 字段，Design Feedback 2026-08-25）
      ...(isSupplierConfirm
        ? [{ formItemKey: FIELD_KEYS.ATTACHMENT, view: false, show: true }]
        : []),
    ];
  }, [detail?.l1_category_id, detail?.initiator_role, isSupplierConfirm, isExternal]);

  return (
    <>
      <RechargeDrawer
        visible={visible}
        onClose={onCancel}
        title="充值单"
        titleExtra={detail && <RechargeStatusTag status={detail.status} />}
        footer={
          <>
            <Button {...rechargeFooterButtonProps} type="checked-neutral" onClick={onCancel}>
              取消
            </Button>
            {canAbandon && (
              <Button {...rechargeFooterButtonProps} danger onClick={handleAbandon} disabled={btnLoading}>
                废弃
              </Button>
            )}
            {canReturn && (
              <Button {...rechargeFooterButtonProps} danger icon={<Undo />} onClick={() => setBackVisible(true)} disabled={btnLoading}>
                退回
              </Button>
            )}
            {canConfirm && (
              <Button {...rechargeFooterButtonProps} type="primary" icon={<Check />} onClick={handleConfirm} loading={btnLoading}>
                {isPendingInitiate ? '重新发起' : '确认'}
              </Button>
            )}
          </>
        }
      >
        {loading ? (
          <CenterLoading />
        ) : (
          <>
            <VerticalLineTitle text="充值单详情" style={{ marginBottom: 16 }} />
            {/* 详情 + 确认操作共用 Form：CurrencyType 金额经 formItemKeyMap 读 form 值，须 Form 上下文 */}
            <Form form={form} layout="vertical">
              <RechargeFormModel
                renderType="descriptions"
                renderData={{ detailData: detail }}
                itemCols={confirmItemCols}
                form={form}
              />

              {detail?.reject_reason && (
                <Alert
                  message={
                    detail.reject_from_state
                      ? `${RECHARGE_STATUS_DESC[detail.reject_from_state as RechargeStateEnum] ?? detail.reject_from_state} 退回`
                      : '已退回'
                  }
                  description={`退回原因：${detail.reject_reason}`}
                  type="warning"
                  showIcon
                  className="mb-4"
                />
              )}

              {canConfirm && (
                <>
                  {!isExternal && showPlannerReason && hasOverCharge && (
                    <SFormItem
                      formItemKey={FIELD_KEYS.OVER_CHARGE_REASON}
                      label="超充原因"
                      required
                      rules={[reasonFieldValidator('请填写超充原因或上传附件')]}
                      validateTrigger={false}
                      render={() => (
                        <TextAreaWithUpload
                          itemKey={FIELD_KEYS.OVER_CHARGE_REASON}
                          maxFileSize={ATTACHMENT_MAX_SIZE}
                          // 抽屉为固定层级（Z_INDEX.drawer），浮层需显式抬高，否则被抽屉自身盖住
                          zIndex={Z_INDEX.drawerPopup}
                          textAreaProps={{
                            maxLength: 500,
                            showCount: true,
                            autoSize: { minRows: 2, maxRows: 6 },
                          }}
                        />
                      )}
                    />
                  )}
                  {!isExternal && showPlannerReason && hasEarlyRecharge && (
                    <SFormItem
                      formItemKey={FIELD_KEYS.EARLY_RECHARGE_REASON}
                      label="提前充值原因"
                      required
                      rules={[reasonFieldValidator('请填写提前充值原因或上传附件')]}
                      validateTrigger={false}
                      render={() => (
                        <TextAreaWithUpload
                          itemKey={FIELD_KEYS.EARLY_RECHARGE_REASON}
                          maxFileSize={ATTACHMENT_MAX_SIZE}
                          // 抽屉为固定层级（Z_INDEX.drawer），浮层需显式抬高，否则被抽屉自身盖住
                          zIndex={Z_INDEX.drawerPopup}
                          textAreaProps={{
                            maxLength: 500,
                            showCount: true,
                            autoSize: { minRows: 2, maxRows: 6 },
                          }}
                        />
                      )}
                    />
                  )}
                  {isExternal && !isSupplierConfirm && (
                    <div className="text-color-3 mb-4">
                      当前为内部审批节点，等待内部审批，供应商无需操作。
                    </div>
                  )}
                  {!isExternal &&
                    !isPlannerConfirm &&
                    !isSupplierConfirm &&
                    isReadonlyConfirm && (
                      <div className="text-color-3 mb-4">
                        请核对充值信息，确认无误后点击确认通过。
                      </div>
                    )}
                  {isPendingInitiate &&
                    !(isPlannerInitiator && (hasOverCharge || hasEarlyRecharge)) && (
                      <div className="text-color-3 mb-4">
                        请确认充值信息，点击重新发起继续流转。
                      </div>
                    )}
                </>
              )}
            </Form>

            {!canConfirm && !canReturn && !canAbandon && (
              <div className="text-color-3 mt-4">
                当前状态（
                {detail?.status
                  ? RECHARGE_STATUS_DESC[detail.status]
                  : (detail?.status_desc ?? '未知')}
                ）无需确认操作。
              </div>
            )}
          </>
        )}
      </RechargeDrawer>

      <SendBackModal
        visible={backVisible}
        loading={btnLoading}
        onCancel={() => setBackVisible(false)}
        onConfirm={handleReject}
      />
    </>
  );
};

// 命令式导出（对齐退款 FormDrawer:467）：RechargeConfirm.show({ order_id, onSuccess })。
// 保留具名裸组件 RechargeConfirmView 供外包 Detail 壳内部渲染。
export default withFunctionCall(RechargeConfirmView);
